import React, { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, AlertTriangle, CheckCircle2, FileArchive } from "lucide-react";
import { uploadReleaseZip, fetchReleases } from "../../lib/files";
import { formatBytes } from "../../lib/files";

export default function ReleaseUploadDialog({ open, onClose, onComplete }) {
  const [releases, setReleases] = useState([]);
  const [release, setRelease] = useState("release_02");
  const [kind, setKind] = useState("docs");
  const [adminToken, setAdminToken] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetchReleases().then((r) => setReleases(r.releases || [])).catch(() => {});
    setError(null);
    setResult(null);
    setProgress(0);
    setFile(null);
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !adminToken.trim()) return;
    setSubmitting(true);
    setError(null);
    setProgress(0);
    try {
      const r = await uploadReleaseZip(file, {
        release,
        kind,
        adminToken: adminToken.trim(),
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setResult(r);
      onComplete?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="release-upload-dialog"
      className="fixed inset-0 z-50 bg-zinc-950/90 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="border border-amber-500/40 bg-zinc-950 w-full max-w-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-100">
              Manual Release Upload
            </span>
          </div>
          <button
            data-testid="upload-dialog-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-amber-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="border border-amber-500/30 bg-amber-500/5 px-3 py-2 mb-4 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-zinc-300 leading-snug">
              war.gov's Akamai edge blocks datacenter IPs with 403. Download the release ZIP in your
              own browser, then upload it here. Requires an admin token.
            </p>
          </div>

          {result ? (
            <div
              data-testid="upload-success"
              className="flex items-start gap-2 border border-emerald-500/40 bg-emerald-500/5 px-3 py-3"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
              <div className="text-sm text-zinc-200">
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-1">
                  Upload accepted
                </div>
                Indexing {result.kind} for <span className="text-amber-400">{result.release}</span>{" "}
                ({formatBytes(result.uploaded_bytes)}). Watch the sync banner below the header for
                progress.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">
                    Release
                  </span>
                  <select
                    data-testid="upload-release-select"
                    value={release}
                    onChange={(e) => setRelease(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-amber-400 focus:outline-none"
                  >
                    {releases.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">
                    Kind
                  </span>
                  <select
                    data-testid="upload-kind-select"
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-amber-400 focus:outline-none"
                  >
                    <option value="docs">Documents</option>
                    <option value="videos">Videos</option>
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">
                  Admin token
                </span>
                <input
                  data-testid="upload-admin-token"
                  type="password"
                  value={adminToken}
                  onChange={(e) => setAdminToken(e.target.value)}
                  placeholder="X-Admin-Token"
                  className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
                  required
                />
              </label>

              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">
                  Release ZIP
                </span>
                <button
                  type="button"
                  data-testid="upload-file-pick"
                  onClick={() => inputRef.current?.click()}
                  className="w-full border border-dashed border-zinc-700 hover:border-amber-400/60 px-4 py-4 text-left flex items-center gap-3 transition-colors"
                >
                  <Upload className="h-4 w-4 text-amber-400" />
                  <div className="flex-1 min-w-0">
                    {file ? (
                      <>
                        <div className="text-sm text-zinc-100 truncate">{file.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">
                          {formatBytes(file.size)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-zinc-100">Choose .zip</div>
                        <div className="font-mono text-[10px] text-zinc-500">
                          e.g. release_02_document_bundle.zip
                        </div>
                      </>
                    )}
                  </div>
                </button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".zip,application/zip"
                  className="hidden"
                  data-testid="upload-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>

              {submitting && (
                <div data-testid="upload-progress">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-2 mb-1">
                    <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
                    Uploading… {progress}%
                  </div>
                  <div className="h-1 bg-zinc-800 relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-amber-400 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div
                  data-testid="upload-error"
                  className="font-mono text-[10px] text-red-400 break-words"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="upload-submit"
                disabled={submitting || !file || !adminToken.trim()}
                className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3" />
                )}
                Upload & ingest
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
